import { colors as qColors } from 'quasar'
import type { colorsRgba } from 'quasar'
import { paletteColors } from 'utils/palette-colors'

/**
 * Interpolate between any given amount of colors (it will find the left and right color value and will interpolate between those 2)
 * @param value Value to be used for interpolation
 * @param args value0, hexColor0, value1, hexColor1, ... (value should be numeric and hexColor '#ffffff')
 * @returns
 */
export const interpolateColors = (value: number, ...args: (number | string)[]) => {
  if (value === null || value === undefined || args.length < 2 || args.length % 2 !== 0) return
  // convert args to array
  let valColorArr: { value: number; hexColor: string }[] = []
  for (let i = 0; i < args.length; i += 2) {
    const hexColor = paletteColors[`${args[i + 1]}`] || `${args[i + 1]}`
    valColorArr.push({
      value: Number(args[i]),
      hexColor
    })
  }
  // filter invalid
  valColorArr = valColorArr.filter(
    (x) => x.value !== null && x.value !== undefined && !isNaN(x.value) && x.hexColor
  )
  // sort array by value
  valColorArr.sort((a, b) => (a.value < b.value ? -1 : 1))
  // if only one value or if value is lower than lowest value in args, return lowest value color
  if (valColorArr.length === 1 || value <= valColorArr[0].value) return valColorArr[0].hexColor
  else if (value >= valColorArr[valColorArr.length - 1].value)
    return valColorArr[valColorArr.length - 1].hexColor
  else {
    const rightIdx = valColorArr.findIndex((x) => x.value >= value) // should not return 0, as this would have been picked up in first if
    const rightValColor = valColorArr[rightIdx]
    const leftValColor = valColorArr[rightIdx - 1]
    const newRgbColor: colorsRgba = findColorBetween(
      qColors.hexToRgb(leftValColor.hexColor), // left
      qColors.hexToRgb(rightValColor.hexColor), // right
      (100 * (value - leftValColor.value)) / (rightValColor.value - leftValColor.value) // percentage
    )
    return qColors.rgbToHex(newRgbColor)
  }
}

/**
 * left and right are colors that you're aiming to find
 * a color between. Percentage (0-100) indicates the ratio
 * of right to left. Higher percentage means more right,
 * lower means more left.
 * @param left rgb object
 * @param right rgb object
 * @param percentage
 * @returns
 */
const findColorBetween = (left: colorsRgba, right: colorsRgba, percentage: number): colorsRgba => {
  return {
    r: Math.round(left.r + ((right.r - left.r) * percentage) / 100),
    g: Math.round(left.g + ((right.g - left.g) * percentage) / 100),
    b: Math.round(left.b + ((right.b - left.b) * percentage) / 100),
    ...(left.a && right.a
      ? { a: Math.round(left.a + ((right.a - left.a) * percentage) / 100) }
      : {})
  }
}
